/**
 * Admin Settings Page - CXO Command Centre
 * User management, logs, and system settings for administrators
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft,
  Users,
  UserPlus,
  Trash2,
  Shield,
  Activity,
  Building2,
  AlertCircle,
  Check,
  X,
  Eye,
  EyeOff,
  Search,
  RefreshCw
} from 'lucide-react';

const AdminSettings = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // New User Form
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/users');
      setUsers(response.data);
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/logs');
      setLogs(response.data);
    } catch (err) {
      setError('Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab, fetchUsers, fetchLogs]);

  // Create new user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await api.post('/api/admin/users', newUser);
      setSuccess('User created successfully');
      setNewUser({ name: '', email: '', password: '', role: 'user', phone: '' });
      setShowNewUserForm(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    setError('');
    setLoading(true);

    try {
      await api.delete(`/api/admin/users/${userId}`);
      setSuccess('User deleted successfully');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  // Toggle user status
  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await api.patch(`/api/admin/users/${userId}`, { is_active: !currentStatus });
      fetchUsers();
    } catch (err) {
      setError('Failed to update user status');
    }
  };

  // Filter users by search
  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'logs', label: 'Activity Logs', icon: Activity },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button 
            style={styles.backButton}
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft size={20} />
          </button>
          <Building2 size={28} style={{ color: '#22d3ee' }} />
          <div>
            <h1 style={styles.headerTitle}>Admin Settings</h1>
            <span style={styles.headerSubtitle}>System Configuration & User Management</span>
          </div>
        </div>
        <div style={styles.headerRight}>
          <Shield size={20} style={{ color: '#22d3ee' }} />
          <span style={styles.adminBadge}>Administrator</span>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        {/* Tabs */}
        <div style={styles.tabs}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.tabActive : {})
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Alerts */}
        {error && (
          <div style={styles.alertError}>
            <AlertCircle size={18} />
            <span>{error}</span>
            <button style={styles.alertClose} onClick={() => setError('')}>
              <X size={16} />
            </button>
          </div>
        )}
        {success && (
          <div style={styles.alertSuccess}>
            <Check size={18} />
            <span>{success}</span>
            <button style={styles.alertClose} onClick={() => setSuccess('')}>
              <X size={16} />
            </button>
          </div>
        )}

        {/* Content */}
        <div style={styles.content}>
          {activeTab === 'users' && (
            <>
              {/* User Management Header */}
              <div style={styles.contentHeader}>
                <div style={styles.searchBox}>
                  <Search size={18} style={{ color: '#94a3b8' }} />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                  />
                </div>
                <div style={styles.headerActions}>
                  <button 
                    style={styles.refreshButton}
                    onClick={fetchUsers}
                    disabled={loading}
                  >
                    <RefreshCw size={18} className={loading ? 'spin' : ''} />
                  </button>
                  <button 
                    style={styles.addButton}
                    onClick={() => setShowNewUserForm(true)}
                  >
                    <UserPlus size={18} />
                    Add New User
                  </button>
                </div>
              </div>

              {/* New User Form */}
              {showNewUserForm && (
                <div style={styles.formCard}>
                  <h3 style={styles.formTitle}>Create New User</h3>
                  <form onSubmit={handleCreateUser}>
                    <div style={styles.formGrid}>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Full Name</label>
                        <input
                          type="text"
                          value={newUser.name}
                          onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                          style={styles.formInput}
                          placeholder="Enter full name"
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Email Address</label>
                        <input
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                          style={styles.formInput}
                          placeholder="Enter email"
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Phone Number (for SMS MFA)</label>
                        <input
                          type="tel"
                          value={newUser.phone}
                          onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                          style={styles.formInput}
                          placeholder="+91 XXXXX XXXXX"
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Password</label>
                        <div style={styles.passwordWrapper}>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={newUser.password}
                            onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                            style={styles.formInput}
                            placeholder="Enter password"
                            required
                          />
                          <button
                            type="button"
                            style={styles.passwordToggle}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Role</label>
                        <select
                          value={newUser.role}
                          onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                          style={styles.formSelect}
                        >
                          <option value="user">User</option>
                          <option value="admin">Administrator</option>
                        </select>
                      </div>
                    </div>
                    <div style={styles.formActions}>
                      <button 
                        type="button" 
                        style={styles.cancelButton}
                        onClick={() => setShowNewUserForm(false)}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        style={styles.submitButton}
                        disabled={loading}
                      >
                        {loading ? 'Creating...' : 'Create User'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Users Table */}
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Name</th>
                      <th style={styles.th}>Email</th>
                      <th style={styles.th}>Role</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>MFA</th>
                      <th style={styles.th}>Last Login</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={styles.emptyRow}>
                          {loading ? 'Loading users...' : 'No users found'}
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u) => (
                        <tr key={u.id} style={styles.tr}>
                          <td style={styles.td}>
                            <div style={styles.userCell}>
                              <div style={styles.avatar}>
                                {u.name?.charAt(0).toUpperCase() || 'U'}
                              </div>
                              {u.name}
                            </div>
                          </td>
                          <td style={styles.td}>{u.email}</td>
                          <td style={styles.td}>
                            <span style={{
                              ...styles.badge,
                              ...(u.role === 'admin' ? styles.badgeAdmin : styles.badgeUser)
                            }}>
                              {u.role === 'admin' ? 'Admin' : 'User'}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <span style={{
                              ...styles.statusBadge,
                              ...(u.is_active ? styles.statusActive : styles.statusInactive)
                            }}>
                              {u.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <span style={{
                              ...styles.mfaBadge,
                              ...(u.is_mfa_enabled ? styles.mfaEnabled : styles.mfaDisabled)
                            }}>
                              {u.is_mfa_enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                          <td style={styles.td}>
                            {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                          </td>
                          <td style={styles.td}>
                            <div style={styles.actions}>
                              <button 
                                style={styles.actionButton}
                                onClick={() => handleToggleStatus(u.id, u.is_active)}
                                title={u.is_active ? 'Deactivate' : 'Activate'}
                              >
                                {u.is_active ? <X size={16} /> : <Check size={16} />}
                              </button>
                              <button 
                                style={{...styles.actionButton, ...styles.deleteButton}}
                                onClick={() => handleDeleteUser(u.id)}
                                title="Delete User"
                                disabled={u.email === user?.email}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {activeTab === 'logs' && (
            <>
              <div style={styles.contentHeader}>
                <h3 style={styles.sectionTitle}>Activity Logs</h3>
                <button 
                  style={styles.refreshButton}
                  onClick={fetchLogs}
                  disabled={loading}
                >
                  <RefreshCw size={18} className={loading ? 'spin' : ''} />
                  Refresh
                </button>
              </div>

              <div style={styles.logsContainer}>
                {logs.length === 0 ? (
                  <div style={styles.emptyLogs}>
                    {loading ? 'Loading logs...' : 'No activity logs found'}
                  </div>
                ) : (
                  logs.map((log, index) => (
                    <div key={index} style={styles.logItem}>
                      <div style={styles.logIcon}>
                        <Activity size={16} />
                      </div>
                      <div style={styles.logContent}>
                        <div style={styles.logHeader}>
                          <span style={styles.logAction}>{log.action}</span>
                          <span style={styles.logTime}>
                            {new Date(log.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <div style={styles.logDetails}>
                          <span>User: {log.user_email}</span>
                          {log.ip_address && <span>IP: {log.ip_address}</span>}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <span>© 2026 CXO Command Centre</span>
        <span style={styles.footerDivider}>|</span>
        <span>Powered by <strong style={{ color: '#22d3ee' }}>Spera Digital ®</strong></span>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#050816',
  },
  header: {
    height: '70px',
    background: 'linear-gradient(135deg, #070b18 0%, #111b3c 55%, #26164b 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 1.5rem',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.45)',
    borderBottom: '1px solid rgba(34, 211, 238, 0.22)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  backButton: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  headerTitle: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '1.25rem',
    fontWeight: '700',
    color: 'white',
    margin: 0,
  },
  headerSubtitle: {
    fontSize: '0.75rem',
    color: 'rgba(255, 255, 255, 0.8)',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  adminBadge: {
    color: '#22d3ee',
    fontSize: '0.875rem',
    fontWeight: '600',
  },
  main: {
    flex: 1,
    padding: '1.5rem',
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto',
  },
  tabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.25rem',
    background: '#0b1022',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    fontWeight: '500',
    color: '#94a3b8',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  tabActive: {
    background: 'linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)',
    color: 'white',
    borderColor: '#22d3ee',
  },
  alertError: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    background: 'rgba(227, 24, 55, 0.1)',
    border: '1px solid rgba(227, 24, 55, 0.3)',
    color: '#e31837',
  },
  alertSuccess: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    background: 'rgba(34, 197, 94, 0.1)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    color: '#16a34a',
  },
  alertClose: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'inherit',
    padding: 0,
  },
  content: {
    background: '#0b1022',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 18px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(34, 211, 238, 0.12)',
  },
  contentHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    background: '#070b18',
    borderRadius: '8px',
    border: '1px solid rgba(148, 163, 184, 0.22)',
  },
  searchInput: {
    border: 'none',
    background: 'none',
    fontSize: '0.9375rem',
    outline: 'none',
    width: '200px',
    color: '#e2e8f0',
  },
  headerActions: {
    display: 'flex',
    gap: '0.75rem',
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    background: '#11182d',
    border: '1px solid rgba(148, 163, 184, 0.22)',
    borderRadius: '8px',
    color: '#94a3b8',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    background: 'linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  formCard: {
    background: '#070b18',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    border: '1px solid rgba(148, 163, 184, 0.22)',
  },
  formTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#e2e8f0',
    marginBottom: '1rem',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
  },
  formGroup: {
    marginBottom: '0.5rem',
  },
  formLabel: {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: '500',
    color: '#cbd5e1',
    marginBottom: '0.375rem',
  },
  formInput: {
    width: '100%',
    padding: '0.625rem 0.875rem',
    fontSize: '0.9375rem',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: '6px',
    outline: 'none',
    background: '#0b1022',
    color: '#e2e8f0',
  },
  formSelect: {
    width: '100%',
    padding: '0.625rem 0.875rem',
    fontSize: '0.9375rem',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: '6px',
    background: '#0b1022',
    color: '#e2e8f0',
    cursor: 'pointer',
  },
  passwordWrapper: {
    position: 'relative',
  },
  passwordToggle: {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: 0,
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  cancelButton: {
    padding: '0.5rem 1.25rem',
    background: '#11182d',
    border: '1px solid rgba(148, 163, 184, 0.22)',
    borderRadius: '6px',
    color: '#94a3b8',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
  submitButton: {
    padding: '0.5rem 1.25rem',
    background: 'linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '6px',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '8px',
    border: '1px solid rgba(148, 163, 184, 0.22)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    background: '#11182d',
    padding: '0.875rem 1rem',
    textAlign: 'left',
    fontSize: '0.8125rem',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
  },
  tr: {
    borderBottom: '1px solid rgba(148, 163, 184, 0.14)',
  },
  td: {
    padding: '0.875rem 1rem',
    fontSize: '0.9375rem',
    color: '#cbd5e1',
  },
  emptyRow: {
    padding: '2rem',
    textAlign: 'center',
    color: '#94a3b8',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.875rem',
    fontWeight: '600',
  },
  badge: {
    padding: '0.25rem 0.625rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
  },
  badgeAdmin: {
    background: 'rgba(197, 160, 40, 0.15)',
    color: '#a08520',
  },
  badgeUser: {
    background: 'rgba(0, 102, 178, 0.1)',
    color: '#0066b2',
  },
  statusBadge: {
    padding: '0.25rem 0.625rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
  },
  statusActive: {
    background: 'rgba(34, 197, 94, 0.1)',
    color: '#16a34a',
  },
  statusInactive: {
    background: 'rgba(227, 24, 55, 0.1)',
    color: '#e31837',
  },
  mfaBadge: {
    padding: '0.25rem 0.625rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
  },
  mfaEnabled: {
    background: 'rgba(34, 197, 94, 0.1)',
    color: '#16a34a',
  },
  mfaDisabled: {
    background: 'rgba(148, 163, 184, 0.2)',
    color: '#64748b',
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  actionButton: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    background: '#11182d',
    border: '1px solid rgba(148, 163, 184, 0.22)',
    color: '#94a3b8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  deleteButton: {
    color: '#e31837',
  },
  sectionTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#e2e8f0',
    margin: 0,
  },
  logsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  emptyLogs: {
    padding: '2rem',
    textAlign: 'center',
    color: '#94a3b8',
    background: '#11182d',
    borderRadius: '8px',
  },
  logItem: {
    display: 'flex',
    gap: '1rem',
    padding: '1rem',
    background: '#11182d',
    borderRadius: '8px',
    border: '1px solid rgba(148, 163, 184, 0.18)',
  },
  logIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: 'rgba(0, 102, 178, 0.1)',
    color: '#0066b2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  logContent: {
    flex: 1,
  },
  logHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.25rem',
  },
  logAction: {
    fontWeight: '500',
    color: '#cbd5e1',
  },
  logTime: {
    fontSize: '0.8125rem',
    color: '#94a3b8',
  },
  logDetails: {
    fontSize: '0.8125rem',
    color: '#64748b',
    display: 'flex',
    gap: '1rem',
  },
  footer: {
    height: '50px',
    background: '#070b18',
    borderTop: '1px solid rgba(34, 211, 238, 0.16)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    fontSize: '0.8125rem',
    color: '#64748b',
  },
  footerDivider: {
    color: '#cbd5e1',
  },
};

export default AdminSettings;
